import {
  AddRounded,
  Check,
  EventNoteRounded,
  QrCodeScannerRounded,
  RefreshRounded,
  ReplayRounded,
  ViewColumnRounded,
} from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  ToggleButton,
  Tooltip,
} from "@mui/material";
import { Filters, InventoryData } from "../../../../../types/inventoryData";
import { useInventory } from "../../../InventoryProvider";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { clearSelectedRows } from "../../../../../helpers/utils";

type ToolbarDesktopTypes = {
  popupToggles: {
    history: boolean;
    addProduct: boolean;
    batchEdit: boolean;
    ganttChart: boolean;
    batchQrScannerToggle: boolean;
    batchEditMode: boolean;
  };
  setPopupToggles: React.Dispatch<
    React.SetStateAction<{
      history: boolean;
      addProduct: boolean;
      batchEdit: boolean;
      ganttChart: boolean;
      batchQrScannerToggle: boolean;
      batchEditMode: boolean;
    }>
  >;
  selectedRows: InventoryData[];
};

const ToolbarDesktop: FC<ToolbarDesktopTypes> = ({
  popupToggles,
  setPopupToggles,
  selectedRows,
}) => {
  const {
    headCells,
    setHeadCells,
    selectedFilterValues,
    setSelectedProductIds,
    setFilteredRows,
  } = useInventory();

  const { t } = useTranslation();
  const [loseSelection, setLoseSelection] = useState<boolean>(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const toggleBatchMode = () => {
    if (selectedRows.length > 0) {
      setLoseSelection(true);
    } else {
      setPopupToggles({
        ...popupToggles,
        batchEditMode: !popupToggles.batchEditMode,
      });
    }
  };

  const renderBatchButtons = () => (
    <>
      <Dialog open={loseSelection} onClose={() => setLoseSelection(false)}>
        <DialogTitle>
          {selectedRows.length} {t("inventory.rowsSelectedExitBatchMode")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("inventory.batchModeSelectionActive")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            variant="contained"
            onClick={() => setLoseSelection(false)}
          >
            {t("inventory.keepSelection")}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setFilteredRows((prev) => clearSelectedRows(prev));
              setPopupToggles({ ...popupToggles, batchEditMode: false });
              setLoseSelection(false);
            }}
          >
            {t("inventory.removeSelection")}
          </Button>
        </DialogActions>
      </Dialog>

      <Button
        variant="contained"
        onClick={() =>
          setPopupToggles({
            ...popupToggles,
            batchQrScannerToggle: true,
          })
        }
        startIcon={<QrCodeScannerRounded />}
      >
        {t("inventory.scanProducts")}
      </Button>
      <Button
        variant="contained"
        disabled={selectedRows.length > 1 ? false : true}
        onClick={() => {
          setSelectedProductIds(selectedRows.map((r) => r.productId));
          setPopupToggles({ ...popupToggles, batchEdit: true });
        }}
      >
        {t("inventory.changeValues")}
      </Button>
    </>
  );

  const renderAddButton = () => (
    <Button
      variant="contained"
      onClick={() => setPopupToggles({ ...popupToggles, addProduct: true })}
      startIcon={<AddRounded />}
    >
      {t("inventory.addProduct")}
    </Button>
  );

  return (
    <Grid container item xs display="flex" height="min-content" gap={1}>
      {popupToggles.batchEditMode ? renderBatchButtons() : renderAddButton()}

      <ToggleButton
        value="check"
        selected={popupToggles.batchEditMode}
        onChange={toggleBatchMode}
      >
        {t("inventory.batchEdit")}
      </ToggleButton>

      <Tooltip title="Refresh">
        <IconButton size="large">
          <RefreshRounded />
        </IconButton>
      </Tooltip>

      <Tooltip title={t("inventory.manageVisibleColumns")}>
        <IconButton
          size="large"
          onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
            setAnchorEl(event.currentTarget)
          }
        >
          <ViewColumnRounded />
        </IconButton>
      </Tooltip>

      <Menu
        id="menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
      >
        <MenuList>
          <MenuItem
            onClick={() =>
              setHeadCells((prev) =>
                prev.map((cell) => ({ ...cell, visible: true }))
              )
            }
          >
            <ListItemIcon>
              <ReplayRounded />
            </ListItemIcon>
            <ListItemText>{t("inventory.Reset")}</ListItemText>
          </MenuItem>

          {headCells.map((h) => {
            if (h.id === "actions") return;
            const filterKey = h.id as keyof Filters;
            const filterDisabled =
              selectedFilterValues[filterKey]?.length !== 0;

            const menuItem = (
              <MenuItem
                key={h.id}
                disabled={filterDisabled}
                onClick={() => {
                  const newHeadCells = headCells.map((cell) =>
                    h === cell ? { ...cell, visible: !cell.visible } : cell
                  );
                  setHeadCells(newHeadCells);
                }}
              >
                <ListItemIcon>{h.visible && <Check />}</ListItemIcon>{" "}
                <ListItemText>{t(h.label)}</ListItemText>
              </MenuItem>
            );

            return filterDisabled ? (
              <Tooltip key={h.id} title={t("inventory.columnHasFilters")}>
                <span>{menuItem}</span>
              </Tooltip>
            ) : (
              menuItem
            );
          })}
        </MenuList>
      </Menu>

      <Tooltip title="Gantt Chart">
        <IconButton
          onClick={() => setPopupToggles({ ...popupToggles, ganttChart: true })}
          size="large"
        >
          <EventNoteRounded />
        </IconButton>
      </Tooltip>
    </Grid>
  );
};

export default ToolbarDesktop;
